import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Globe, Loader2 } from 'lucide-react'
import { getLanguageByCode } from '../../lib/constants/languages'
import type { LibraryBookTranslation, CEFRLevel } from '../../types'

interface VersionSelectorProps {
  // Available languages with their translations
  translations: Record<string, LibraryBookTranslation[]>
  availableLanguages: string[]
  // Current selection
  selectedLanguage: string
  selectedLevel: string | null
  // Callbacks
  onLanguageChange: (language: string) => void
  onLevelChange: (level: string | null) => void
  // User's preferred language for pre-selection
  userPreferredLanguage?: string
}

const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

export function VersionSelector({
  translations,
  availableLanguages,
  selectedLanguage,
  selectedLevel,
  onLanguageChange,
  onLevelChange,
  userPreferredLanguage: _userPreferredLanguage
}: VersionSelectorProps) {
  const { t } = useTranslation('common')

  const languageOptions = Object.keys(translations || {})
  const currentTranslations = selectedLanguage ? translations[selectedLanguage] || [] : []

  // Get available levels for the selected language
  const availableLevels = new Set(
    currentTranslations
      .filter(t => t.status === 'available' && t.cefr_level)
      .map(t => t.cefr_level as string)
  )

  // Get processing levels for the selected language
  const processingLevels = new Set(
    currentTranslations
      .filter(t => t.status === 'processing' && t.cefr_level)
      .map(t => t.cefr_level as string)
  )

  const getLanguageDisplayName = (langCode: string): string => {
    const lang = getLanguageByCode(langCode)
    return lang ? lang.name : langCode
  }

  const handleLevelClick = (level: string) => {
    if (availableLevels.has(level)) {
      if (selectedLevel === level) {
        onLevelChange(null) // Deselect
      } else {
        onLevelChange(level)
      }
    }
  }

  const isLanguageAvailable = (langCode: string) => availableLanguages.includes(langCode)

  return (
    <div className="version-selector space-y-4">
      {/* Language Dropdown */}
      <div className="language-selector">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
          <Globe className="h-4 w-4" />
          {t('common.selectLanguage')}
        </label>
        <Select value={selectedLanguage} onValueChange={onLanguageChange}>
          <SelectTrigger className="language-dropdown w-full sm:w-64">
            <SelectValue placeholder={t('library.selectLanguage', 'Select language')} />
          </SelectTrigger>
          <SelectContent>
            {languageOptions.map((lang) => {
              const isAvailable = isLanguageAvailable(lang)
              return (
                <SelectItem key={lang} value={lang}>
                  <div className="flex items-center gap-2">
                    <span>{getLanguageDisplayName(lang)}</span>
                    {!isAvailable && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Level Chips */}
      {selectedLanguage && (
        <div className="level-selector">
          <label className="text-sm font-medium text-foreground mb-2 block">
            {t('library.selectLevel')}
          </label>
          <div className="level-chips flex flex-wrap gap-2">
            {CEFR_LEVELS.map((level) => {
              const isAvailable = availableLevels.has(level)
              const isProcessing = processingLevels.has(level)
              const isSelected = selectedLevel === level

              return (
                <button
                  key={level}
                  onClick={() => handleLevelClick(level)}
                  disabled={!isAvailable}
                  className={`level-chip px-3 py-1.5 rounded-full text-sm font-medium transition-all
                    ${isSelected 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : isAvailable
                        ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                        : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                    }
                  `}
                >
                  <span className="flex items-center gap-1">
                    {level}
                    {isProcessing && (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                  </span>
                </button>
              )
            })}
          </div>
          {!availableLevels.size && selectedLanguage && (
            <p className="text-xs text-muted-foreground mt-2">
              {t('library.noLevelsAvailable', 'No levels available for this language yet')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

